import {
  ButtonFinal,
  CallContinue,
  EnableMicrophone,
  HeaderDisplayScreen,
  HeaderLeftConference,
  HeaderPopup,
  Icons,
  LayoutSelector,
  LayoutType,
  PedagogyActionOrigin,
  Popup,
  ReactIcons,
  ScreenPopup,
  Switch,
} from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleGreaterThan, isSuperUser } from "@kalyzee/kast-web-sdk";
import { ChatMessageData, Touchable } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingCallState, MeetingOnExcludeEvent, MeetingRoomStatus, MeetingSession, MeetingSessionRole, MeetingSessionUIAction } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import DashboardChat from "../../../../components/dashboard/Chat";
import HighlightRequestsList from "../../../../components/dashboard/HighlightRequestsList";
import ModeratorCallsList from "../../../../components/dashboard/ModeratorCallsList";
import { GroupViewerExpertContainer } from "../../../../components/groups/expert/GroupViewerExpertContainer";
import { PageMasterMode, PageOptions, usePageContextRef } from "../../../../components/navigation/PageContext";
import { ExpertsToRemotesContainer } from "../../../../components/player/container/ExpertsToRemotesContainer";
import MeetingPlayerStyleContainer from "../../../../components/player/container/MeetingPlayerStyleContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import MeetingPlayerSoundEffectOverlay from "../../../../components/player/MeetingPlayerSoundEffectOverlay";
import MeetingSessionPlayer from "../../../../components/player/MeetingSessionPlayer";
import { PlayersGrid, PlayersGridRef } from "../../../../components/player/PlayersGrid";
import {
  getExpertActor,
  getFullName,
  startSessionHighlight,
  stopSessionHighlight,
  updateSosMode,
  userToEscaetUser,
} from "../../../../helpers/meeting";
import { addPopup, removePopup, removePopupByGroup, removePopupByTag } from "../../../../helpers/popup";
import { toastError } from "../../../../helpers/toast";
import {
  RestrictedAudioPermission,
  useMeetingContainerAPI,
  useMeetingSelector,
  usePermission,
  useStandaloneHighlightedMeetingSessions,
} from "../../../../hooks/meeting";
import { usePopulatedMeetingData } from "../../../../hooks/populatedData";
import { useReduxAction } from "../../../../hooks/store";
import socketMeetingActions from "../../../../store/meeting/actions";
import { useUser } from "../../../../store/session/hooks";
import { SessionMode } from "../../../../store/session/slices";
import { useExpertContext } from "../context";
import styles from "./dashboard.module.css";
import { BadBehaviorsCounter } from "../../../../components/behaviors/BadBehaviorsCounter";
import { usePedagogyActions } from "../../pedagogy/actions";
import { ConfirmableEscaetPedagogyAction, EscaetPedagogyAction, MeetingPedagogyUserData } from "../../../../interfaces/pedagogy";
import { PedagogyMessageForm } from "../../../../components/pedagogy/MessageForm";
import { sdkClient } from "../../../../helpers/sdk";

// Negative value to disable it
const DELAY_SHOW_CALL_POPUP_AT_START = -1;  //5 * 60 * 1000; // 5 minutes
const DELAY_SHOW_CALL_POPUP_AGAIN = 5000;  //5 * 60 * 1000; // 5 minutes

export const MeetingDashboardExpertPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const expertContext = useExpertContext();

  const meetingContainerAPI = useMeetingContainerAPI(pageContextRef.current?.params?.id);

  const [meeting, meetingRef] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(meetingContainerAPI, (state) => state.session);
  const [receptionSessions] = useMeetingSelector(meetingContainerAPI, (state) => state.receptionSessions);
  const [surveys] = useMeetingSelector(meetingContainerAPI, (state) => state.surveys);

  const gridRef = useRef<PlayersGridRef>(null);
  const callTimoutRef = useRef<NodeJS.Timeout>();
  const [sessionIdForUserPopup, setSessionIdForUserPopup] = useState<MeetingSession["id"]>();
  const [layout, setLayout] = useState(LayoutType.Square);

  const user = useUser();

  const { pending: pendingHighlightingSessions } = useStandaloneHighlightedMeetingSessions(meetingContainerAPI);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);

  const { studentUsers, rooms } = populatedMeetingData ?? {};
  const groups = rooms?.groups;

  const permissionAPI = usePermission(meeting);

  const sosMode = !!populatedMeetingData?.me?.data?.sosMode;

  const connectedStudentSessions = useMemo(() => {
    return expertContext?.orderedSessions?.filter((s) => s.connected) ?? [];
  }, [expertContext?.orderedSessions]);

  // Actions
  const [confirmableAction, setConfirmableAction] = useState<ConfirmableEscaetPedagogyAction>();
  const pedagogyActions = usePedagogyActions(
    undefined,
    undefined,
    undefined,
    useCallback((action: ConfirmableEscaetPedagogyAction) => {
      setConfirmableAction(action);
    }, [])
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onClick = useCallback((msg: ChatMessageData) => {
    const session = meetingRef.current?.sessions?.find((s) => s.user?.id === msg.user.id);
    if (!session) return;
    setSessionIdForUserPopup(session.id);
  }, []);

  const onSos = useCallback(() => {
    if (!meeting) return;
    updateSosMode(meeting.id, !sosMode);
    removePopupByTag("meeting_sos_popup");
  }, [meeting, sosMode]);

  const onCall = (isExitCall?: boolean) => {
    const id = pageContextRef.current?.params?.id ?? "";
    const options: PageOptions = {};
    if (isExitCall) options.queries = { exit: "true" };
    pageContextRef.current?.goTo(PageMasterMode.MEETING_CALL, { id }, options);
  };

  const showCallPopup = () => {
    const meeting = meetingRef.current;
    if (!meeting) return;
    const popupTag = "meeting_call_popup";
    removePopupByTag(popupTag); /// remove if already exist

    const callOngoing = meeting.callState && meeting.callState !== MeetingCallState.CLOSED;
    const callWasDone = meeting.callWasDone || meeting.endCallWasDone;

    let title = callOngoing ? "Reprendre l'appel" : "Faire l'appel";
    let description = "Faites l’appel pour recenser les effectifs présents en classe et à distance";

    if (callWasDone && !meeting.callMultipleEnabled) {
      title = "Appel déjà effectué";
      description = "L'appel a déjà été effectué pour cette session et la fonctionnalité d'appel multiple n'est pas activée.";
    }

    const popup = (
      <Popup
        style={{
          width: "fit-content",
          height: "fit-content",
          padding: "2em",
        }}
        Header={
          <HeaderPopup
            className={styles.callWasDoneHeader}
            icon={Icons.BlueHand}
            title={title}
            description={description}
          />
        }
        Content={
          callOngoing ? (
            // A call is already ongoing
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <ButtonFinal
                text={"Revenir au dashboard"}
                textColor="#82817f"
                background="#f1efeb"
                cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[580px]"
                onClick={() => {
                  removePopup(popup);
                }}
              />
              <ButtonFinal
                text={meeting.callState === MeetingCallState.OPEN_START ? "Reprendre l'appel d'entrée" : "Reprendre l'appel de sortie"}
                textColor="#fff"
                background="rgb(0, 144, 255)"
                cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[580px]"
                onClick={() => {
                  removePopup(popup);
                  onCall();
                }}
              />
            </div>
          ) : (
            // No call is oingoing
            callWasDone && !meeting.callMultipleEnabled ? null : (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <CallContinue
                  onClickCancel={() => {
                    if (callTimoutRef.current) {
                      // Expert ends the call popup reminder
                      // And refuses to to a call
                      clearTimeout(callTimoutRef.current);
                      callTimoutRef.current = undefined;
                      const sessionId = sessionRef.current?.id;
                      if (!sessionId) return;
                      sdkClient.getWebsocketClient().emitter.meeting.submitUIAction({
                        sessionId,
                        action: MeetingSessionUIAction.RefuseStartCall,
                      });
                    }
                    removePopup(popup);
                  }}
                  onClickReminder={callWasDone ? undefined : () => {
                    if (DELAY_SHOW_CALL_POPUP_AGAIN > 0) {
                      callTimoutRef.current = setTimeout(() => {
                        clearTimeout(callTimoutRef.current);
                        callTimoutRef.current = undefined;
                        // remind
                        showCallPopup();
                      }, DELAY_SHOW_CALL_POPUP_AGAIN);
                    }
                    const sessionId = sessionRef.current?.id;
                    if (!sessionId) return;
                    sdkClient.getWebsocketClient().emitter.meeting.submitUIAction({
                      sessionId,
                      action: MeetingSessionUIAction.PostponeCall,
                    });
                    removePopup(popup);
                  }}
                  onCall={() => {
                    removePopup(popup);
                    onCall();
                  }}
                  onEndCall={meeting.endCallEnabled ? () => {
                    removePopup(popup);
                    onCall(true);
                  } : undefined}
                />
              </div>
            )
          )
        }
      />
    );
    addPopup(popup, popupTag, "dashboard");
  };

  const showSOSPopup = () => {
    const popup = (
      <Popup
        onClose={() => removePopup(popup)}
        style={{
          width: "min(800px, 90vw)",
          height: "fit-content",
          padding: "2em",
        }}
        Header={<></>}
        Content={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4em",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5em",
                alignItems: "center",
              }}
            >
              <ReactIcons.HeadphonePopup style={{ width: 70, height: 70 }} />
              <div style={{ fontWeight: 800, fontSize: "2em" }}>Sos pédagogie, j'ai besoin d'aide</div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#E6E3DD",
                padding: "0.5em",
                borderRadius: 999,
              }}
            >
              <ButtonFinal
                text={sosMode ? "Annuler l’appel du service pédagogique" : "Contacter le service pédagogique"}
                image={sosMode ? Icons.CancelHelp : Icons.CallPopup}
                background="#ffffff"
                onClick={onSos}
                cx="!pt-[5px] !pr-[35px] !pb-[5px] !pl-[5px] font-bold rounded-[50px] !text-[20px]"
              />
            </div>
          </div>
        }
      />
    );
    addPopup(popup, "meeting_sos_popup", "dashboard");
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   REDUX ACTION ----------------------------- //
  // ---------------------------------------------------------------------- //

  useReduxAction<MeetingOnExcludeEvent>(
    (action) => {
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      toastError("Vous avez été exclus.");
    },
    socketMeetingActions.onExclude,
    []
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  const ongoingGroups = useMemo(() => groups?.filter((g) => g.room.status === MeetingRoomStatus.Ongoing) ?? [], [groups]);

  useEffect(() => {
    /**
     * TODO
     * we need to allow audio in those cases :
     * - session highlight
     * - group highlight
     * - ongoing group
     */
    if (ongoingGroups.length) {
      const restrictedAudio = permissionAPI.hasPermission("audio", RestrictedAudioPermission);
      if (restrictedAudio) {
        permissionAPI.setPermission("audio", RestrictedAudioPermission, false);
      }
    }
  }, [ongoingGroups, permissionAPI]);

  useEffect(() => {
    return () => {
      removePopupByGroup("dashboard");
      if (callTimoutRef.current) {
        clearTimeout(callTimoutRef.current);
        callTimoutRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (meeting?.startedAt && !meeting.callWasDone && !meeting.endCallWasDone && !callTimoutRef.current) {
      const now = new Date().getTime();
      const startDate = meeting.startedAt ? new Date(meeting.startedAt) : undefined;
      if (DELAY_SHOW_CALL_POPUP_AT_START > 0) {
        if (startDate && startDate.getTime() + DELAY_SHOW_CALL_POPUP_AT_START > now) {
          callTimoutRef.current = setTimeout(() => {
            // remind
            showCallPopup();
          }, startDate.getTime() + DELAY_SHOW_CALL_POPUP_AT_START - now);
        }
      }
    }
  }, [meeting?.startedAt, meeting?.callWasDone, meeting?.endCallWasDone]);

  useEffect(() => {
    if (pendingHighlightingSessions.length === 0) {
      expertContext?.setHighlitghedRequestsPanelOpen(false);
    }
  }, [pendingHighlightingSessions]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting || !session || !user || !rooms) return null;

  const renderNavigationBox = () => {
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          backgroundColor: "#22222210",
          borderRadius: 12,
          gap: 10,
          padding: 8,
        }}
      >
        {!ongoingGroups.length && (
          <Touchable
            onPress={() => pageContextRef.current?.goTo(PageMasterMode.MEETING_GROUP, { id: pageContextRef.current.params?.id })}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              backgroundColor: "white",
              borderRadius: 8,
              padding: 12,
              gap: 12,
              color: "#0090FF",
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            <ReactIcons.Group style={{ width: 68 }} />
            <div>Groupes</div>
          </Touchable>
        )}
        <Touchable
          className={surveys?.length ? styles.surveyBlink : undefined}
          onPress={() => pageContextRef.current?.goTo(PageMasterMode.MEETING_SURVEY, { id: pageContextRef.current.params?.id })}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            backgroundColor: surveys?.length ? "#0090FF" : "white",
            borderRadius: 8,
            padding: 12,
            gap: 12,
            color: surveys?.length ? "white" : "#0090FF",
            fontSize: 16,
            fontWeight: 800,
          }}
        >
          <div style={{
            width: 68,
            height: 68,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: surveys?.length ? "white" : "#0090FF",
            borderRadius: 999,
          }}>
            <ReactIcons.Sondage />
          </div>
          <div>Sondage</div>
        </Touchable>
      </div>
    );
  };

  const renderLeftPanel = () => {
    const expert = getExpertActor(meeting);
    const nameSplitted = meeting.name?.split("-") ?? [];
    const promo = nameSplitted.length > 1 ? nameSplitted[0].trim() : undefined;
    const chatroom = meeting.chatrooms?.[0];
    return (
      <div className={styles.leftContainer}>
        {
          <HeaderLeftConference
            userInformation={{
              profilePicture: expert?.user?.avatar ?? null,
              label: promo ?? "",
              role: getFullName(expert?.user?.firstname, expert?.user?.lastname),
            }}
            buttons={[
              <ButtonFinal 
                text={meeting?.callState && meeting.callState !== MeetingCallState.CLOSED ? "Reprendre l'appel" : "Faire l'appel"}
                image={Icons.BlueHand} 
                background="#ffffff" 
                onClick={showCallPopup} 
                cx={styles.callBtn} 
              />,
              <ButtonFinal
                text="SOS pédagogie"
                image={Icons.Headphone}
                background="#ffffff"
                onClick={showSOSPopup}
                cx={`${styles.sosBtn} ${sosMode ? styles.active : ""}`}
              />,
            ]}
          />
        }
        <HighlightRequestsList
          className={`${styles.highlightRequestsContainer} ${styles.blockContainer}`}
          meetingContainer={meetingContainerAPI}
          mode={ongoingGroups?.length ? "groups_only" : "users_only"}
        />
        <ModeratorCallsList className={`${styles.moderatorCallsContainer} ${styles.blockContainer}`} meetingData={populatedMeetingData} />
        {chatroom && (
          <DashboardChat
            chatroom={chatroom}
            className={`${styles.chatContainer} ${styles.blockContainer}`}
            meetingContainer={meetingContainerAPI}
            hideSendBar
            disableDeletion={false}
            onClick={onClick}
          />
        )}
        {renderNavigationBox()}
      </div>
    );
  };

  const renderMicrophoneButton = () => {
    const checked = !permissionAPI.hasPermission("audio", RestrictedAudioPermission);
    if (ongoingGroups?.length) return null; // don't show microphone button if there are ongoing groups
    return (
      <EnableMicrophone
        icon={Icons.Microphone}
        text="Autoriser l'activation des micros"
        component={
          <Switch
            // color="yellowgreen"
            name="microphone"
            checked={checked}
            onChange={() => permissionAPI.setPermission("audio", RestrictedAudioPermission, checked)}
            color={checked ? "#05C496" : "#F05E5E"}
            sliderColor={"white"}
          />
        }
        style={{
          borderRadius: "50px",
          padding: "5px 10px 5px 10px",
          fontSize: "12px",
          fontWeight: "bold",
          gap: "20px",
        }}
      />
    );
  };

  const renderMainPanel = () => {
    const sessions = expertContext?.orderedSessions ?? [];
    const rows = 4;
    const columns = 4;

    const renderButtons = () => {
      return (
        <div className={styles.mainButtonContainer}>
          {renderMicrophoneButton()}
          <BadBehaviorsCounter
            sessions={sessions}
          />
        </div>
      );
    };

    if (ongoingGroups.length && studentUsers) {
      return (
        <div className={styles.mainContainer}>
          <HeaderDisplayScreen icon={Icons.Person} title={"Groupes"} style={{ width: "100%" }} nbr={ongoingGroups.length} />
          <GroupViewerExpertContainer
            meetingContainerAPI={meetingContainerAPI}
            context={expertContext}
            users={studentUsers}
            rooms={rooms}
            session={session}
            selfUserId={user.id}
          />
        </div>
      );
    }
    return (
      <div className={styles.mainContainer}>
        <HeaderDisplayScreen icon={Icons.Person} title={"Public"} style={{ width: "100%" }} nbr={connectedStudentSessions.length} />
        {sessions.length ? (
          <PlayersGrid
            ref={gridRef}
            name="STANDALONE"
            style={{ width: "100%", padding: "20px", flex: "1", minHeight: "0px" }}
            meeting={meeting}
            gridProps={{
              column: columns,
              row: rows,
              autoAdjust: false,
            }}
            items={
              sessions?.map(
                (s) =>
                  new PlayerItem({
                    type: "meeting_session",
                    meetingSession: s.session,
                    videoSessions: receptionSessions?.filter((s2) => s2.data.session.id === s.session.id) ?? [],
                    online: s.connected,
                  })
              ) ?? []
            }
            renderItem={(renderDefaultPlayer, item) => {
              const itemData = item?.getData();
              if (itemData?.type === "meeting_session") {
                const clickable = itemData.online !== false;
                return renderDefaultPlayer((children, context) => (
                  <MeetingPlayerStyleContainer
                    mode="dashboard"
                    meeting={meeting}
                    session={itemData.meetingSession}
                    style={{ width: "100%", height: "100%", cursor: clickable ? "pointer" : undefined }}
                    isPlayingSound={context?.isPlayingSound}
                    nativeProps={{
                      onClick: () => {
                        if (!clickable) return;
                        setSessionIdForUserPopup(itemData.meetingSession.id);
                      },
                    }}
                  >
                    {children}
                    <MeetingPlayerSoundEffectOverlay enabled={context?.isPlayingSound} />
                  </MeetingPlayerStyleContainer>
                ));
              }
              return <div />;
            }}
            defaultRendererProviderOptions={{
              muted: true,
              hideSoundEffect: true,
            }}
            autoOnOffVideo={true}
          />
        ) : (
          <div className={styles.noParticipants}>Aucun participant</div>
        )}
        {renderButtons()}
      </div>
    );
  };

  const renderHeaderHighligt = () => {
    const currentView = expertContext?.currentView;
    if (currentView?.type !== "highlight") return null;
    const studentsData = currentView.students;
    if (!studentsData?.length) return null;

    const groupNamesSet = new Set(
      studentsData.filter((data) => data.video && data.session.user.group?.room.status === MeetingRoomStatus.Ongoing).map((data) => data.session.user?.group?.room.name)
    );
    const groupNames = Array.from(groupNamesSet);

    let isGroupMode = true;
    let message: string;
    if (groupNames.length === 1) {
      message = `Groupe ${groupNames[0]} `;
    } else if (groupNames.length > 1) {
      message = `Groupes ${groupNames.join(", ")} `; //
    } else {
      isGroupMode = false;
      message = "";
    }
    message += "au tableau";

    const displayLayoutSelector = (isGroupMode && groupNames.length > 1) || studentsData.length > 1;

    const checked = !permissionAPI.hasPermission("audio", RestrictedAudioPermission);
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "stretch",
          position: "relative",
        }}
      >
        <div style={{
          marginTop: 15,
          marginLeft: 30,
        }}>
          {renderMicrophoneButton()}  
        </div>
        {displayLayoutSelector ? (
          <LayoutSelector
            style={{
              flexDirection: "row",
              color: "black",
              padding: "0.5em 1.25em",
              backgroundColor: "#2222221A",
              borderRadius: 999,
              gap: "0.75em",
              marginTop: 15,
              marginRight: 30,
            }}
            iconSize="s"
            selected={layout}
            onChange={setLayout}
            enabledOptions={[LayoutType.Square, LayoutType.HorizontalSquare, LayoutType.VerticalSquare]}
          />
        ) : <div />}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#0090FF",
            padding: "20px 50px 10px 50px",
            borderRadius: "0px 0px 20px 20px",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {message.toUpperCase()}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (expertContext?.currentView?.type === "highlight") {
      return (
        <>
          <ExpertsToRemotesContainer
            meetingContainerAPI={meetingContainerAPI}
            context={expertContext}
            expertSessions={expertContext.currentView.experts.filter((data) => data.video).map((data) => data.session)}
            remoteSessions={expertContext.currentView.students.filter((data) => data.video).map((data) => data.session)}
            layout={layout}
            showSelf="new_source"
            renderItem={(item, children) => (
              <>
                {children}
                {item.getData().extra?.meetingSession?.user.group?.room.status !== MeetingRoomStatus.Ongoing ? (
                  <Touchable
                    style={{
                      position: "absolute",
                      color: "white",
                      fontWeight: "bold",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#22222280",
                      top: "7px",
                      right: "7px",
                      cursor: "pointer",
                      zIndex: 1,
                    }}
                    onPress={(_, event) => {
                      const session = item.getData().extra?.meetingSession;
                      if (session) stopSessionHighlight(session.session.id);
                      event?.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    X
                  </Touchable>
                ) : null}
              </>
            )}
            onClickSession={(session) => setSessionIdForUserPopup(session.id)}
          />
          {expertContext?.highlitghedRequestsPanelOpen && (
            <HighlightRequestsList
              onClose={() => expertContext.setHighlitghedRequestsPanelOpen(false)}
              meetingContainer={meetingContainerAPI}
              className={styles.blockContainer}
              style={{
                height: 400,
                position: "absolute",
                bottom: 10,
                left: 40,
                zIndex: 1,
                backgroundColor: "rgb(223 221 216)",
              }}
              mode={ongoingGroups?.length ? "groups_only" : "users_only"}
            />
          )}
        </>
      );
    }
    return (
      <>
        {renderLeftPanel()}
        {renderMainPanel()}
      </>
    );
  };

  const renderUserPopup = () => {
    if (!sessionIdForUserPopup) return;
    const session = meeting.sessions?.find((s) => s.id === sessionIdForUserPopup);
    if (!session) {
      setSessionIdForUserPopup(undefined);
      return null;
    }

    const currView = expertContext?.currentView;

    const highlighting = currView?.type === "highlight" ? currView.students?.find((s) => s.session.id === session.id) : undefined;
    const isMicrophoneActive = session.medias?.[0]?.audio.enabled ?? false;
    const superUser = isSuperUser(sessionRef.current);
    const moderator = sessionRef.current ? isMeetingSessionRoleGreaterThan(sessionRef.current?.role, MeetingSessionRole.MODERATOR) : false;

    const executePedagogyAction = (type: EscaetPedagogyAction["type"]) => {
      if (!session.user) return;
      const currUser: MeetingPedagogyUserData = {
        ...userToEscaetUser(session.user),
        meetingId: meeting.id,
      };
      const action = pedagogyActions.find((a) => a.type === type);
      if (!action) return;
      action.onExecute([currUser], PedagogyActionOrigin.Other);
    };

    return (
      <ScreenPopup
        isMicrophoneActive={isMicrophoneActive}
        onToggleMicrophone={
          superUser
            ? async () => {
              if (isMicrophoneActive) meetingContainerAPI?.mute({ sessions: [{ id: session.id }] });
              else meetingContainerAPI?.unmute({ sessions: [{ id: session.id }] });
            }
            : undefined
        }
        onExclude={async () => {
          executePedagogyAction("exclude");
          setSessionIdForUserPopup(undefined);
          return true;
        }}
        onWriteWarning={async () => {
          executePedagogyAction("warn");
          setSessionIdForUserPopup(undefined);
          return true;
        }}
        onBringUpBoard={
          moderator && !highlighting
            ? async () => {
              try {
                startSessionHighlight(session.id);
              } catch (error) {
                return false;
              }
              return true;
            }
            : undefined
        }
        onClose={() => {
          setSessionIdForUserPopup(undefined);
        }}
      >
        <MeetingPlayerStyleContainer mode="dashboard" meeting={meeting} session={session} style={{ width: "100%", height: "60vh" }}>
          <MeetingSessionPlayer
            style={{ width: "100%", height: "100%" }}
            meeting={meeting}
            sessionId={session.id}
            ovenMediaSessions={receptionSessions}
            muted={true}
          />
        </MeetingPlayerStyleContainer>
      </ScreenPopup>
    );
  };

  return (
    <>
      {expertContext?.currentView?.type === "highlight" ? renderHeaderHighligt() : null}
      <div className={styles.container}>
        {renderContent()}
        {renderUserPopup()}
        {confirmableAction ? (
          <PedagogyMessageForm
            actions={pedagogyActions}
            confirmableAction={confirmableAction}
            onClose={() => {
              setConfirmableAction(undefined);
            }}
            predefinedMessages={{
              "exclude": [{
                label: "Perturbateur",
                message: "L'élève pertube le cours",
              }, {
                label: "Inattentif",
                message: "L'élève est inattentif",
              },  {
                label: "Caméra off",
                message: "L'élève n'a pas sa caméra d'allumée",
              }, {
                label: "Inapproprié",
                message: "L'élève a un comportement, une tenue ou travaille dans un contexte inapproprié",
              }],
              "warn": [{
                label: "Perturbateur",
                message: "L'élève pertube le cours",
              }, {
                label: "Inattentif",
                message: "L'élève est inattentif",
              },  {
                label: "Caméra off",
                message: "L'élève n'a pas sa caméra d'allumée",
              }, {
                label: "Inapproprié",
                message: "L'élève a un comportement, une tenue ou travaille dans un contexte inapproprié",
              }],
            }}
          />
        ) : null}
      </div>
    </>
  );
};
